import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Celebration } from "@/components/Celebration";
import { Crown, Check, ChevronLeft, Clock, MessageSquare, Globe, Sparkles, Receipt } from "lucide-react";
import { toast } from "sonner";
import { startCheckout } from "@/lib/payments";

const PERKS = [
  { icon: Clock, title: "60s Call Extensions", desc: "Add +30 seconds to every vibe check" },
  { icon: MessageSquare, title: "Double texting limits", desc: "20 messages per match before the call" },
  { icon: Globe, title: "Global Passport Mode", desc: "Browse international events anywhere" },
];

const TIERS = [
  { id: "monthly", label: "Clapham Speed Pass", price: "£12.99", per: "/month", note: "Unlimited calls & messages · renews monthly, cancel anytime", best: true },
  { id: "vibe_pack", label: "5 Speed Token Pack", price: "£1.99", per: " one-off", note: "Each Speed Token = 1 extra vibe check + 10 messages, or 1 contact card swap" },
];

function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export default function Premium() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const qc = useQueryClient();
  const [selected, setSelected] = useState("monthly");
  const [busy, setBusy] = useState(false);
  const [celebrate, setCelebrate] = useState(false);
  const [renewBusy, setRenewBusy] = useState(false);
  const [billing, setBilling] = useState([]);
  const active = !!user?.premium;

  const loadBilling = useCallback(async () => {
    try {
      const { data } = await api.get("/premium/billing");
      setBilling(data || []);
    } catch {
      setBilling([]);
    }
  }, []);

  useEffect(() => { loadBilling(); }, [loadBilling]);

  const upgrade = async () => {
    setBusy(true);
    try {
      await startCheckout(selected === "vibe_pack" ? "vibe_pack" : "speed_pass");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not activate premium");
    } finally {
      setBusy(false);
    }
  };

  const toggleAutoRenew = async () => {
    const next = !user?.auto_renew;
    setRenewBusy(true);
    try {
      await api.post("/premium/auto-renew", { enabled: next });
      await refresh();
      toast.success(next ? "Auto-renew on — your pass won't lapse" : "Auto-renew off");
    } catch {
      toast.error("Could not update auto-renew");
    } finally {
      setRenewBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#070d09] flex flex-col" data-testid="premium-page">
      <Celebration show={celebrate} onDone={() => setCelebrate(false)} />
      {/* Ambient glow */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-24 -left-20 h-72 w-72 rounded-full bg-amber-500/15 blur-3xl" />
        <div className="absolute top-40 -right-16 h-72 w-72 rounded-full bg-pink-500/10 blur-3xl" />
      </div>

      <div className="relative flex-1 w-full max-w-lg mx-auto px-5 pt-6 pb-40">
        <button data-testid="premium-back" onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-200 transition-colors">
          <ChevronLeft className="h-4 w-4" /> Back
        </button>

        {/* Header */}
        <div className="text-center mt-8 fade-up">
          <div className="h-16 w-16 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center mx-auto mb-5 pulse-ring">
            <Crown className="h-8 w-8 text-amber-400" />
          </div>
          <span className="chip inline-block text-[11px] font-semibold uppercase tracking-widest px-3 py-1 rounded-full">MoYo Stars Date Premium</span>
          <h1 className="font-display font-black text-4xl sm:text-5xl mt-4 gold-text leading-tight" data-testid="premium-title">Unlock the Vibe</h1>
          <p className="text-sm text-slate-300 mt-3 max-w-sm mx-auto leading-relaxed">More time, more messages, more events. Turn every spark into a real plan.</p>
        </div>

        {/* Active plan banner */}
        {active && (
          <div className="glass rounded-3xl p-5 mt-8 border-amber-500/40 flex items-center gap-3 fade-up" data-testid="premium-active-banner">
            <span className="h-11 w-11 shrink-0 rounded-full bg-amber-500/20 border border-amber-400/40 flex items-center justify-center">
              <Crown className="h-5 w-5 text-amber-400" />
            </span>
            <div>
              <p className="text-sm font-semibold gold-text">Premium active · {user.premium_plan === "weekly" ? "Weekly Pass" : "Monthly Pass"}</p>
              <p className="text-xs text-slate-400 mt-0.5">{user.auto_renew ? "Renews" : "Expires"} {fmtDate(user.premium_until)}</p>
            </div>
          </div>
        )}

        {/* Auto-renew toggle */}
        {active && (
          <div className="glass rounded-2xl px-5 py-4 mt-4 flex items-center justify-between fade-up" data-testid="auto-renew-row">
            <div className="pr-4">
              <p className="text-sm font-semibold text-slate-200">Auto-renew</p>
              <p className="text-xs text-slate-400 mt-0.5">Keep your perks without interruption — renews automatically before it expires.</p>
            </div>
            <button
              data-testid="auto-renew-toggle"
              role="switch"
              aria-checked={!!user.auto_renew}
              disabled={renewBusy}
              onClick={toggleAutoRenew}
              className={`relative h-7 w-12 shrink-0 rounded-full transition-colors disabled:opacity-60 ${user.auto_renew ? "bg-amber-400" : "bg-white/15"}`}
            >
              <span className={`absolute top-1 h-5 w-5 rounded-full bg-[#0b130e] transition-all ${user.auto_renew ? "left-6" : "left-1"}`} />
            </button>
          </div>
        )}

        {/* Feature breakdown */}
        <div className="glass rounded-3xl p-6 mt-8 fade-up" data-testid="premium-perks">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-pink-400" /> What you get
          </h2>
          <ul className="mt-4 space-y-4">
            {PERKS.map((p) => (
              <li key={p.title} className="flex items-start gap-3" data-testid={`premium-perk-${p.title.toLowerCase().replace(/\s+/g, "-")}`}>
                <span className="mt-0.5 h-7 w-7 shrink-0 rounded-full bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center">
                  <Check className="h-4 w-4 text-emerald-300" />
                </span>
                <div>
                  <p className="text-sm font-semibold text-slate-200">{p.title}</p>
                  <p className="text-sm text-slate-400 leading-relaxed">{p.desc}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Billing history */}
        {billing.length > 0 && (
          <div className="glass rounded-3xl p-6 mt-8 fade-up" data-testid="billing-history">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Receipt className="h-4 w-4 text-amber-400" /> Billing history
            </h2>
            <ul className="mt-4 divide-y divide-white/5">
              {billing.map((b, i) => (
                <li key={b.id || i} className="flex items-center justify-between py-3" data-testid="billing-row">
                  <div>
                    <p className="text-sm font-semibold text-slate-200">
                      {b.plan_label} · {b.type === "renewal" ? "Renewal" : "New subscription"}
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5">{fmtDate(b.created_at)}</p>
                  </div>
                  <span className="font-display font-bold text-sm gold-text">£{b.amount}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Pricing tiers */}
        {!active && (
          <div className="mt-8 fade-up">
            <h2 className="text-sm font-semibold uppercase tracking-widest text-amber-400 mb-4">Choose your pass</h2>
            <div className="space-y-3">
              {TIERS.map((t) => {
                const on = selected === t.id;
                return (
                  <button
                    key={t.id}
                    data-testid={`premium-tier-${t.id}`}
                    onClick={() => setSelected(t.id)}
                    className={`relative w-full text-left rounded-2xl p-5 border transition-all duration-300 ${
                      on
                        ? "border-amber-400/70 bg-amber-500/10 shadow-[0_10px_34px_-10px_rgba(245,158,11,0.55)]"
                        : "border-white/10 bg-white/[0.03] hover:border-amber-500/30"
                    }`}
                  >
                    {t.best && (
                      <span className="absolute -top-2.5 right-4 btn-gold text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full" data-testid="premium-best-badge">
                        Best value
                      </span>
                    )}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className={`h-5 w-5 rounded-full border-2 flex items-center justify-center transition-colors ${on ? "border-amber-400 bg-amber-400" : "border-white/25"}`}>
                          {on && <Check className="h-3 w-3 text-[#1a1203]" strokeWidth={3} />}
                        </span>
                        <div>
                          <p className="font-display font-bold text-lg text-white">{t.label}</p>
                          <p className="text-xs text-slate-400 mt-0.5">{t.note}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`font-display font-black text-xl ${on ? "gold-text" : "text-slate-200"}`}>{t.price}</span>
                        <span className="text-xs text-slate-400 block">{t.per}</span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Sticky action button */}
      <div className="fixed bottom-0 inset-x-0 bg-gradient-to-t from-[#070d09] via-[#070d09] to-transparent pt-10 pb-6 px-5">
        <div className="max-w-lg mx-auto">
          {active ? (
            <Button data-testid="premium-done" onClick={() => navigate("/discover")} className="w-full btn-gold rounded-full h-14 text-base">
              <Sparkles className="h-5 w-5 mr-2" /> Explore global events
            </Button>
          ) : (
            <Button data-testid="premium-upgrade-now" disabled={busy} onClick={upgrade} className="w-full btn-gold rounded-full h-14 text-base disabled:opacity-60">
              <Crown className="h-5 w-5 mr-2" /> {busy ? "Activating…" : "Upgrade Now"}
            </Button>
          )}
          <p className="text-center text-[11px] text-slate-500 mt-3">Cancel anytime · Secure checkout · No hidden fees</p>
        </div>
      </div>
    </div>
  );
}
